import { Popover, PopoverContent, PopoverTrigger } from "@gigpro/ui/Popover";
import { cn } from "@gigpro/ui/tailwind/cn";
import { useBreakpoint } from "@gigpro/ui/utils/useBreakpoint";
import { useNavigate } from "@tanstack/react-router";
import { useContextLocations } from "api/queries/locations";
import { useAuth } from "domains/auth";
import { OrganizationCombobox } from "domains/orgs/components/OrganizationCombobox";
import { useActiveLocation, useManagedOrganizations, useStoredLocation } from "services/locationOrgContext";

type OrganizationSwitcherProps = React.ComponentProps<typeof PopoverTrigger>;

function sortActiveFirst<T extends { id: number | string; name: string }>(
  list: Array<T> | undefined,
  activeId: number | string | undefined,
) {
  return list?.toSorted((a, b) => {
    if (a.id === activeId) return -1;
    if (b.id === activeId) return 1;
    return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
  });
}

export function OrganizationSwitcher({ children, className, ...rest }: Readonly<OrganizationSwitcherProps>) {
  const isDesktop = useBreakpoint("md");

  return (
    <Popover modal>
      <PopoverTrigger className={cn("rounded-sm", className)} data-testid={"organization-combobox"} {...rest}>
        {children}
      </PopoverTrigger>
      <PopoverContent
        collisionPadding={0}
        className="w-72 max-w-[calc(100vw-var(--spacing)*4)]"
        side={isDesktop ? "bottom" : "top"}
        align="end"
      >
        <OrganizationComboboxContainer />
      </PopoverContent>
    </Popover>
  );
}

function OrganizationComboboxContainer() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const orgData = useManagedOrganizations();

  const [viewLocation, setActiveLocationId] = useActiveLocation();
  const storedLocation = useStoredLocation() ?? viewLocation;
  const storedOrganization = orgData?.find((org) => org.id === storedLocation?.organization_id) ?? orgData?.[0];

  const { data: allLocations } = useContextLocations({ includeAll: true });
  const orgLocations = allLocations?.filter((location) => location.organization_id === storedOrganization?.id);

  const viewingLocationName =
    viewLocation && storedLocation && viewLocation.id !== storedLocation.id ? viewLocation.name : undefined;

  return (
    <OrganizationCombobox
      user={user}
      data-testid={"organization-combobox"}
      onLogout={logout}
      activeLocation={storedLocation}
      viewingLocationName={viewingLocationName}
      locations={sortActiveFirst(orgLocations, storedLocation?.id)}
      hiddenLocations={sortActiveFirst(
        allLocations?.filter((location) => location.organization_id !== storedOrganization?.id),
        undefined,
      )}
      onSelectLocation={(location) => setActiveLocationId(location.id)}
      onManageLocations={() => navigate({ to: "/locations" })}
      activeOrganization={storedOrganization}
      organizations={sortActiveFirst(orgData, storedOrganization?.id)}
      onSelectOrganization={(org) => {
        const newLocation = allLocations?.find((location) => location.organization_id === org.id)?.id;
        if (newLocation) setActiveLocationId(newLocation);
      }}
    />
  );
}
